import { useState, useMemo, useEffect, useRef } from "react";
import axios from "axios";
import { format } from "date-fns";
import { CalendarIcon, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { SERVICES, TIME_SLOTS } from "@/data/services";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputClasses =
  "bg-white/5 border-white/10 text-white placeholder:text-white/35 focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37] rounded-lg h-11";

export default function BookingDialog({ open, onOpenChange, initialService }) {
  const [step, setStep] = useState("form"); // form | success
  const [submitting, setSubmitting] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [serviceId, setServiceId] = useState(initialService?.id || "doorbell");
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef(null);

  // Reset on open with new service
  const selectedService = useMemo(
    () => SERVICES.find((s) => s.id === serviceId) || SERVICES[0],
    [serviceId]
  );

  // Sync initialService when reopened
  useEffect(() => {
    if (open && initialService?.id) setServiceId(initialService.id);
    if (open) {
      setStep("form");
      setShowCalendar(false);
    }
  }, [open, initialService]);

  // Close inline calendar when clicking outside of it (but keep dialog open)
  useEffect(() => {
    if (!showCalendar) return;
    const handler = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setShowCalendar(false);
      }
    };
    // small defer so the opening click doesn't immediately close it
    const t = setTimeout(() => document.addEventListener("mousedown", handler), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", handler);
    };
  }, [showCalendar]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const validate = () => {
    if (!serviceId) return "Please choose a service.";
    if (!date) return "Please pick a date.";
    if (!time) return "Please pick a time slot.";
    if (!name.trim()) return "Please enter your full name.";
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email))
      return "Please enter a valid email.";
    if (!phone.trim() || phone.replace(/\D/g, "").length < 7)
      return "Please enter a valid phone number.";
    if (!address.trim()) return "Please enter your service address.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        service_id: serviceId,
        customer_name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        address: address.trim(),
        booking_date: format(date, "yyyy-MM-dd"),
        booking_time: time,
        notes: notes.trim(),
      };
      const res = await axios.post(`${API}/bookings`, payload);
      setBookingId(res.data?.id || null);
      toast.success("Booking confirmed! Check your inbox.");
      setStep("success");
    } catch (error) {
      const detail =
        error?.response?.data?.detail ||
        "Couldn't submit your booking. Please try again or call us.";
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setName("");
    setEmail("");
    setPhone("");
    setAddress("");
    setNotes("");
    setDate(undefined);
    setTime("");
    setStep("form");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-dialog"
        className="bg-[#121216] border-white/10 text-white max-w-2xl max-h-[92vh] overflow-y-auto p-0 rounded-2xl"
      >
        {step === "form" && (
          <>
            <DialogHeader className="px-6 md:px-8 pt-7 pb-4 border-b border-white/10">
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-semibold">
                Schedule Service
              </p>
              <DialogTitle className="text-2xl md:text-3xl font-semibold text-white tracking-tight mt-1">
                Book your {selectedService.title.toLowerCase().replace(" installation", "")}.
              </DialogTitle>
              <DialogDescription className="text-white/55 text-sm">
                Takes under a minute. We'll confirm by phone within hours.
              </DialogDescription>
            </DialogHeader>

            <form
              onSubmit={handleSubmit}
              className="px-6 md:px-8 py-6 space-y-5"
              data-testid="booking-form"
            >
              {/* Service */}
              <div className="space-y-2">
                <Label className="text-white/80">Service</Label>
                <Select value={serviceId} onValueChange={setServiceId}>
                  <SelectTrigger
                    data-testid="booking-service-select"
                    className={cn(inputClasses, "w-full")}
                  >
                    <SelectValue placeholder="Choose a service" />
                  </SelectTrigger>
                  <SelectContent
                    className="bg-[#1A1A20] border-white/10 text-white"
                  >
                    {SERVICES.map((s) => (
                      <SelectItem
                        key={s.id}
                        value={s.id}
                        data-testid={`service-option-${s.id}`}
                        className="focus:bg-[#D4AF37]/15 focus:text-white"
                      >
                        {s.title} · ${s.starting_price}+
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date + time */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2" ref={calendarRef}>
                  <Label className="text-white/80">Date</Label>
                  <Button
                    type="button"
                    data-testid="booking-date-trigger"
                    variant="outline"
                    onClick={() => setShowCalendar((v) => !v)}
                    className={cn(
                      inputClasses,
                      "justify-start text-left font-normal w-full hover:bg-white/10",
                      !date && "text-white/40"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-[#D4AF37]" />
                    {date ? format(date, "EEE, MMM d, yyyy") : "Pick a date"}
                  </Button>
                  {showCalendar && (
                    <div
                      data-testid="booking-calendar-wrap"
                      className="mt-2 rounded-lg bg-[#1A1A20] border border-white/10 shadow-xl p-1"
                    >
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={(d) => {
                          setDate(d);
                          if (d) setShowCalendar(false);
                        }}
                        disabled={(d) => {
                          const dayStart = new Date(d);
                          dayStart.setHours(0, 0, 0, 0);
                          return dayStart < today;
                        }}
                        initialFocus
                        data-testid="booking-calendar"
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-white/80">Time</Label>
                  <Select value={time} onValueChange={setTime}>
                    <SelectTrigger
                      data-testid="booking-time-select"
                      className={cn(inputClasses, "w-full")}
                    >
                      <SelectValue placeholder="Select a time" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A20] border-white/10 text-white">
                      {TIME_SLOTS.map((t) => (
                        <SelectItem
                          key={t}
                          value={t}
                          data-testid={`time-option-${t.replace(/[\s:]/g, "")}`}
                          className="focus:bg-[#D4AF37]/15 focus:text-white"
                        >
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Contact */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bk-name" className="text-white/80">Full name</Label>
                  <Input
                    id="bk-name"
                    data-testid="booking-name-input"
                    placeholder="Jane Smith"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={inputClasses}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bk-email" className="text-white/80">Email</Label>
                  <Input
                    id="bk-email"
                    type="email"
                    data-testid="booking-email-input"
                    placeholder="jane@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClasses}
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bk-phone" className="text-white/80">Phone</Label>
                  <Input
                    id="bk-phone"
                    data-testid="booking-phone-input"
                    placeholder="(404) 555-0199"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className={inputClasses}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bk-address" className="text-white/80">Service address</Label>
                  <Input
                    id="bk-address"
                    data-testid="booking-address-input"
                    placeholder="123 Peachtree St, Atlanta GA"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className={inputClasses}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bk-notes" className="text-white/80">
                  Notes <span className="text-white/40">(optional)</span>
                </Label>
                <Textarea
                  id="bk-notes"
                  data-testid="booking-notes-input"
                  placeholder="Anything we should know — gate codes, mounting height preferences, etc."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className={cn(inputClasses, "h-auto py-3")}
                />
              </div>

              <div className="flex items-center justify-between gap-3 pt-2 flex-wrap">
                <div className="text-sm text-white/55">
                  Starting at{" "}
                  <span className="text-[#D4AF37] font-semibold">
                    ${selectedService.starting_price}
                  </span>{" "}
                  · Final quote on-site
                </div>
                <Button
                  type="submit"
                  data-testid="booking-submit-button"
                  disabled={submitting}
                  className="bg-[#D4AF37] text-black hover:bg-[#C5A017] font-semibold rounded-full h-11 px-7 min-w-[170px]"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Booking...
                    </>
                  ) : (
                    "Confirm Booking"
                  )}
                </Button>
              </div>
            </form>
          </>
        )}

        {step === "success" && (
          <div
            data-testid="booking-success"
            className="px-6 md:px-10 py-12 text-center"
          >
            <div className="mx-auto mb-6 grid place-items-center w-16 h-16 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40">
              <CheckCircle2 className="text-[#D4AF37]" size={30} />
            </div>
            <h3 className="text-2xl md:text-3xl font-semibold tracking-tight text-white">
              Appointment received.
            </h3>
            <p className="mt-3 text-white/65 text-base leading-relaxed max-w-md mx-auto">
              Thanks, <span className="text-white">{name.split(" ")[0] || "there"}</span>. A specialist will follow up
              shortly at <span className="text-white">{email}</span> or <span className="text-white">{phone}</span> to
              confirm the exact time and share any pre-visit details.
            </p>
            <div className="mt-6 inline-flex flex-col items-center gap-1 text-sm text-white/55">
              <span>Need to reach us sooner?</span>
              <a
                href="tel:4708080832"
                className="text-[#D4AF37] hover:underline font-semibold"
              >
                470-808-0832
              </a>
            </div>
            <div className="mt-8 flex items-center justify-center">
              <Button
                data-testid="booking-success-close"
                onClick={resetAndClose}
                className="bg-[#D4AF37] text-black hover:bg-[#C5A017] font-semibold rounded-full h-11 px-7"
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
